import { Location } from "history";
import * as R from "ramda";
import * as React from "react";
import { PulseLoader } from "react-spinners";
import styled from "styled-components";

import AnalyticsPage from "../../../components/pages/Analytics";
import GraphsPage from "../../../components/pages/Analytics/GraphsPage";
import Error from "../../../components/pages/Error";
import Spinner from "../../../components/utils/Spinner";
import { timeTakenFormatter } from "../../../formatters/analytics";
import fontSizes from "../../../styles/fontSizes";
import { LocaleNames, PreferredLocale } from "../../../utils/locales";
import { IClass, ICourse, IScormPackage } from "../../types/common";
import { FetchClassDataRequestState } from "../ClassAnalytics/reducer";
import {
  AssessmentChartFilterLabels,
  AssessmentStatus,
  AssessmentType,
  FetchPartInstanceAnalytics,
  IUnitInstanceAnalytics,
  IUnitsAnalytics,
  IUser,
  IUserFilter,
  IUsersAnalytics,
  TopicsChartFilter,
  UnitChartType,
  UserFilter,
} from "../Common";
import {
  getSelectedUsers,
  getUserAssessmentFilter,
  removeFilter,
  removeLabelAndCondition,
  toggleFilter,
  updateFilterConditions,
  updateFilters,
  userAccountMatches,
  userNameMatches,
  userTimeTakenBetween,
  userTookTopic,
} from "../Common/filters";
import {
  FetchAllUsersRequestState,
  FetchCourseDataRequestState,
  FetchScormPackageDataRequestState,
  FetchUsersAnalyticsRequestState,
} from "../ScormPackageAnalytics/reducer";
import UsersPage from "../../../components/pages/Analytics/UsersTablePage/UsersPage";

import Highcharts from "highcharts";
import HighchartsStock from "highcharts/highstock";

/* tslint:disable-next-line */
import exporting from "highcharts/modules/exporting.js";
import { AnalyticsViews } from "../../../App/Analytics/Common/index";
import { ClassIcon } from "../../../components/utils/Icons/Analytics/TitleIcons";
import { ClosedPackageIcon, OpenPackageIcon } from "../../../components/utils/Icons/PackageIcon";

/* tslint:disable-next-line */
import exportData from "highcharts/modules/export-data.js";

/* tslint:disable */
exporting(Highcharts);
exporting(HighchartsStock);
exportData(Highcharts);
exportData(HighchartsStock);
/* tslint:enable */

export interface IScormPackageAnalyticsOwnProps {
  courseId: string;
  objectId: string;
  fetchPartInstanceAnalytics: FetchPartInstanceAnalytics;
}

export interface IAnalyticsStateProps {
  fetchAllUsersRequestState: FetchAllUsersRequestState;
  fetchScormPackageDataRequestState: FetchScormPackageDataRequestState | null;
  fetchClassDataRequestState: FetchClassDataRequestState | null;
  fetchCourseDataRequestState: FetchCourseDataRequestState;
  fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState;
  course: ICourse | null;
  class: IClass | null;
  scormPackage: IScormPackage | null;
  unitsAnalytics: IUnitsAnalytics[] | null;
  users: IUser[] | null;
  usersAnalytics: IUsersAnalytics | null;
  location: Location;
}

export interface IAnalyticsDispatchProps {
  mount: () => void;
  unmount: () => void;
  print: () => void;
}

export interface IAnalyticsState {
  assessmentChartLabels: AssessmentChartFilterLabels[];
  filters: IUserFilter[];
  nameFilter: string;
  selectedAssessmentIdentifier: string | null;
  selectedAssessmentName: string | null;
  selectedAssessmentType: UnitChartType | null;
  selectedInTable: Set<string>;
  selectedLocale: PreferredLocale | undefined;
  selectedUnitName: string | null;
  selectedUserAccounts: Set<string>;
  showFilteredTable: boolean;
  topicsChartSelected: number[];
  unitInstanceAnalytics: IUnitInstanceAnalytics;
  useSelectedUsersInTable: boolean;
  view: AnalyticsViews;
}

export type IAnalyticsProps = IScormPackageAnalyticsOwnProps & IAnalyticsStateProps & IAnalyticsDispatchProps;

type ArrayValue = string | number;

const LoadingDiv = styled.div`
  flex: 1;
  flex-direction: row;
  margin: 2.5rem auto;
  span {
    display: flex;
    justify-content: center;
    font-size: ${fontSizes.large};
  }
`;

const LoadingMessage: React.FunctionComponent<{ msg: string }> = ({ msg }) => (
  <LoadingDiv>
    <span>
      {msg} <PulseLoader color="black" size={4} />
    </span>
  </LoadingDiv>
);
export default class Analytics extends React.Component<IAnalyticsProps, IAnalyticsState> {
  constructor(props: IAnalyticsProps) {
    super(props);
    const searchParams = new URLSearchParams(this.props.location.search);
    const viewParam = searchParams.get("view");
    const view: AnalyticsViews = viewParam ? (viewParam as AnalyticsViews) : AnalyticsViews.Graphs;
    this.state = {
      assessmentChartLabels: [],
      filters: [],
      nameFilter: "",
      selectedAssessmentIdentifier: null,
      selectedAssessmentName: null,
      selectedAssessmentType: null,
      selectedInTable: new Set(),
      selectedLocale: undefined,
      selectedUnitName: null,
      selectedUserAccounts: new Set(),
      showFilteredTable: true,
      topicsChartSelected: [],
      unitInstanceAnalytics: {},
      useSelectedUsersInTable: false,
      view,
    };
  }

  public componentDidMount() {
    this.props.mount();
  }

  public componentDidUpdate(prevProps: IAnalyticsProps, prevState: {}) {
    if (prevProps.courseId !== this.props.courseId || prevProps.objectId !== this.props.objectId) {
      this.props.unmount();
      this.props.mount();
    }
  }

  public componentWillUnmount() {
    this.props.unmount();
  }

  public render() {
    const {
      fetchCourseDataRequestState,
      fetchAllUsersRequestState,
      fetchUsersAnalyticsRequestState,
      fetchScormPackageDataRequestState,
      fetchPartInstanceAnalytics,
    } = this.props;
    const isPending =
      this.getRequestDataState() ||
      fetchCourseDataRequestState === FetchCourseDataRequestState.IDLE ||
      fetchCourseDataRequestState === FetchCourseDataRequestState.PENDING ||
      fetchCourseDataRequestState === FetchCourseDataRequestState.FAILED ||
      fetchAllUsersRequestState === FetchAllUsersRequestState.IDLE ||
      fetchAllUsersRequestState === FetchAllUsersRequestState.PENDING ||
      fetchUsersAnalyticsRequestState === FetchUsersAnalyticsRequestState.IDLE ||
      fetchUsersAnalyticsRequestState === FetchUsersAnalyticsRequestState.PENDING;
    if (isPending) {
      return (
        <>
          <Spinner />
          {fetchCourseDataRequestState !== FetchCourseDataRequestState.SUCCEEDED && (
            <LoadingMessage msg="Loading course data" />
          )}
          {fetchAllUsersRequestState !== FetchAllUsersRequestState.SUCCEEDED && <LoadingMessage msg="Loading users" />}
          {fetchUsersAnalyticsRequestState !== FetchUsersAnalyticsRequestState.SUCCEEDED && (
            <LoadingMessage msg="Loading course analytics" />
          )}
        </>
      );
    }
    const isFailed =
      fetchCourseDataRequestState === FetchCourseDataRequestState.FAILED ||
      fetchScormPackageDataRequestState === FetchScormPackageDataRequestState.FAILED ||
      fetchAllUsersRequestState === FetchAllUsersRequestState.FAILED ||
      fetchUsersAnalyticsRequestState === FetchUsersAnalyticsRequestState.FAILED;
    if (isFailed) {
      return <Error />;
    }
    const {
      assessmentChartLabels,
      filters,
      nameFilter,
      selectedInTable,
      selectedUserAccounts,
      selectedUnitName,
      useSelectedUsersInTable,
      selectedLocale,
      selectedAssessmentIdentifier,
      selectedAssessmentName,
      selectedAssessmentType,
      unitInstanceAnalytics,
      view,
    } = this.state;
    const { course, print, users, usersAnalytics, unitsAnalytics } = this.props;
    const onlyEnabledFilters = filters.filter(f => f.enabled);

    return (
      /* tslint:disable jsx-no-lambda */
      <AnalyticsPage
        selectedCount={selectedUserAccounts.size}
        allUsersCount={users === null ? 0 : users.length}
        courseDisplayName={this.getCourseName()}
        objectDisplayName={this.getNameForDisplay()}
        onPrint={print}
        filters={onlyEnabledFilters}
        onRemoveFilter={this.onRemoveFilter}
        onClearAllFilters={this.onClearAllFilters}
        onRemoveLabelAndCondition={this.onUpdateFilterLabelAndCondition}
        selectedUnitName={selectedUnitName}
        backToClassStatistics={() => {
          this.onSelectUnitName(null)();
          this.selectAssessmentName(null, null, null)();
        }}
        selectedAssessmentName={selectedAssessmentName}
        backToUnitAnalytics={this.selectAssessmentName(null, null, null)}
        changeAnalyticsView={this.changeAnalyticsView}
        view={view}
        classOrScormIcon={this.getIconBasedOnClassOrPackage()}
        users={users !== null ? users : []}
        usersAnalytics={usersAnalytics !== null ? usersAnalytics : {}}
        selected={selectedInTable}
        selectedFromGraph={selectedUserAccounts}
        showFilteredTable={this.state.showFilteredTable}
        toggleShowFilteredTable={this.onShowFilteredChange}
      >
        {view === AnalyticsViews.Graphs && (
          <GraphsPage
            onRemoveFilter={this.onRemoveFilter}
            assessmentChartLabels={assessmentChartLabels}
            users={users || []}
            usersAnalytics={this.getAnalyticsForCalculation()}
            unitsAnalytics={unitsAnalytics || []}
            selected={selectedUserAccounts}
            useSelectedAsFilter={onlyEnabledFilters.length > 0}
            timeTakenChartFilter={this.handleTimeTakenChartFilter}
            assessmentsChartFilter={this.handleAssessmentsChartFilter}
            topicsChartFilter={this.handleTopicsChartFilter}
            onSelectUnitName={this.onSelectUnitName}
            selectedUnitName={selectedUnitName}
            fetchExerciseAnalytics={fetchPartInstanceAnalytics}
            supportedLocales={course ? course.supportedLocales : []}
            onSelectLocale={this.onSelectLocale}
            selectedLocale={selectedLocale}
            masterLocale={course ? course.masterLocale : PreferredLocale.en}
            onSelectAssessmentName={this.selectAssessmentName}
            selectedAssessmentIdentifier={selectedAssessmentIdentifier}
            selectedAssessmentName={selectedAssessmentName}
            selectedAssessmentType={selectedAssessmentType}
            unitInstanceAnalytics={unitInstanceAnalytics}
            courseDisplayName={this.getCourseName()}
            objectDisplayName={this.getNameForDisplay()}
            courseId={this.getCourseId()}
          />
        )}
        {view === AnalyticsViews.Users && (
          <UsersPage
            nameFilter={nameFilter}
            useSelectedAsFilter={useSelectedUsersInTable}
            selected={selectedInTable}
            selectedFromGraph={selectedUserAccounts}
            onToggleSelectAll={this.onToggleSelectAll}
            onToggleSelect={this.onToggleSelect}
            onUseSelectedAsFilterChange={this.onUseSelectedAsFilterChange}
            onNameFilterChange={this.onNameFilterChange}
            users={users !== null ? users : []}
            usersAnalytics={usersAnalytics !== null ? usersAnalytics : {}}
            showFilteredTable={this.state.showFilteredTable}
          />
        )}
      </AnalyticsPage>
      /* tslint:enable:jsx-no-lambda */
    );
  }

  public onShowFilteredChange = () => {
    this.setState({ showFilteredTable: !this.state.showFilteredTable });
  };

  public onToggleSelectAll = () => {
    const users = this.props.users || [];
    if (this.state.selectedInTable.size !== users.length) {
      this.setState(
        {
          selectedInTable: new Set(users.map(u => u.accountId)),
        },
        this.handleToggleSelect
      );
    } else {
      const filters = removeFilter(this.getFilterName(), UserFilter.MarkedInTable, this.state.filters);
      this.setState(
        prevState => ({
          ...prevState,
          filters,
          selectedInTable: new Set(),
          showFilteredTable: true,
          useSelectedUsersInTable: false,
        }),
        this.updateSelectedUsers
      );

      this.setState({ selectedInTable: new Set() }, this.handleToggleSelect);
    }
  };

  public onToggleSelect = (accountId: string) => () => {
    if (this.state.selectedInTable.has(accountId)) {
      if (this.state.selectedInTable.size === 1) {
        const filters = removeFilter(this.getFilterName(), UserFilter.MarkedInTable, this.state.filters);
        this.setState(
          prevState => ({
            ...prevState,
            filters,
            showFilteredTable: true,
            useSelectedUsersInTable: false,
          }),
          this.updateSelectedUsers
        );
      }

      this.state.selectedInTable.delete(accountId);
    } else {
      this.state.selectedInTable.add(accountId);
    }
    this.setState({ selectedInTable: new Set(this.state.selectedInTable) }, this.handleToggleSelect);
  };

  public onNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nameFilter = e.target.value;
    const filters = this.handleNameFilterChange(nameFilter);
    this.setState({ filters, nameFilter }, this.updateSelectedUsers);
  };

  public onUseSelectedAsFilterChange = () => {
    const useSelected = !this.state.useSelectedUsersInTable;
    const filters =
      this.state.selectedInTable.size === 0
        ? removeFilter(this.getFilterName(), UserFilter.MarkedInTable, this.state.filters)
        : toggleFilter(this.getFilterName(), UserFilter.MarkedInTable, this.state.filters);

    this.setState(
      prevState => ({
        ...prevState,
        filters,
        showFilteredTable: !useSelected,
        useSelectedUsersInTable: useSelected,
      }),
      this.updateSelectedUsers
    );
  };

  public onClearAllFilters = () => {
    const onlyMarkedUsersFilter = this.state.filters.filter(f => f.type === UserFilter.MarkedInTable);
    const filters = toggleFilter(this.getFilterName(), UserFilter.MarkedInTable, onlyMarkedUsersFilter);
    this.setState(
      {
        assessmentChartLabels: [],
        filters,
        selectedUserAccounts: new Set(),
        showFilteredTable: true,
        topicsChartSelected: [],
        useSelectedUsersInTable: false,
      },
      this.updateSelectedUsers
    );
  };

  public onRemoveFilter = (name: string, type: UserFilter) => () => {
    const filters = this.getFiltersFromRemove(name, type);
    const showFilteredNewState = filters.length === 1;
    const commonState = { filters, showFilteredTable: showFilteredNewState };
    if (UserFilter.AssessmentState === type) {
      this.setState({ ...commonState, assessmentChartLabels: [] }, this.updateSelectedUsers);
    } else if (UserFilter.TookTopic === type) {
      this.setState({ ...commonState, topicsChartSelected: [] }, this.updateSelectedUsers);
    } else {
      this.setState({ ...commonState }, this.updateSelectedUsers);
    }
  };

  private onUpdateFilterLabelAndCondition = (name: string, type: UserFilter) => (index: number) => () => {
    const filters = removeLabelAndCondition(name, type, index, this.state.filters);
    if (UserFilter.AssessmentState === type) {
      this.updateOrRemoveItemFromArray(filters, this.getItemFromArray(type, index), "assessmentChartLabels");
    } else if (UserFilter.TookTopic === type) {
      this.updateOrRemoveItemFromArray(filters, Number(this.getItemFromArray(type, index)), "topicsChartSelected");
    } else {
      this.setState({ filters }, this.updateSelectedUsers);
    }
  };

  private getItemFromArray = (type: UserFilter, index: number) => {
    for (const filter of this.state.filters) {
      if (filter.type === type) {
        return filter.labels[index];
      }
    }
    return "";
  };

  private handleToggleSelect = () => {
    const filters = updateFilters(
      this.getFilterName(),
      UserFilter.MarkedInTable,
      Array.from(this.state.selectedInTable).map(s => userAccountMatches(s)),
      this.state.useSelectedUsersInTable,
      [],
      this.state.filters
    );
    this.setState({ filters }, this.updateSelectedUsers);
  };

  private handleNameFilterChange = (searchName: string) => {
    if (searchName === "") {
      return removeFilter(this.getFilterName(), UserFilter.NameMatches, this.state.filters);
    }
    return updateFilters(
      this.getFilterName(),
      UserFilter.NameMatches,
      [userNameMatches(searchName)],
      true,
      [searchName],
      this.state.filters
    );
  };

  private getFiltersFromRemove = (name: string, type: UserFilter) => {
    const { filters } = this.state;
    if (UserFilter.MarkedInTable === type) {
      this.setState({ useSelectedUsersInTable: false });
      return toggleFilter(name, type, filters);
    }
    return removeFilter(name, type, filters);
  };

  private updateSelectedUsers() {
    // Changing this to a promise improves load times and makes animation smooth
    const updateSelectedUsersPromise = new Promise(res => {
      const selectedUserAccounts = new Set(getSelectedUsers(this.props.users, this.state.filters));
      return res({ selectedUserAccounts });
    });
    updateSelectedUsersPromise.then((res: { selectedUserAccounts: Set<string> }) => this.setState({ ...res }));
  }

  private getRequestDataState() {
    const { fetchScormPackageDataRequestState, fetchClassDataRequestState } = this.props;
    if (fetchScormPackageDataRequestState !== null) {
      return (
        fetchScormPackageDataRequestState === FetchScormPackageDataRequestState.IDLE ||
        fetchScormPackageDataRequestState === FetchScormPackageDataRequestState.PENDING ||
        fetchScormPackageDataRequestState === FetchScormPackageDataRequestState.FAILED
      );
    }
    if (fetchClassDataRequestState !== null) {
      return (
        fetchClassDataRequestState === FetchClassDataRequestState.IDLE ||
        fetchClassDataRequestState === FetchClassDataRequestState.PENDING ||
        fetchClassDataRequestState === FetchClassDataRequestState.FAILED
      );
    }
    return true;
  }

  private getNameForDisplay() {
    const { scormPackage } = this.props;
    if (scormPackage !== null) {
      return scormPackage.name;
    }
    const { class: classObj } = this.props;
    if (classObj !== null) {
      return classObj.displayName;
    }
    return "";
  }

  private getIconBasedOnClassOrPackage() {
    const { scormPackage } = this.props;
    if (scormPackage !== null) {
      scormPackage.state === "closed" ? <ClosedPackageIcon /> : <OpenPackageIcon />;
    }
    return <ClassIcon />;
  }

  private getCourseName() {
    return this.props.course !== null ? this.props.course.displayName : "";
  }

  private getCourseId() {
    return this.props.course?.courseId ?? "";
  }

  private handleTimeTakenChartFilter = (minTime: number, maxTime: number) => {
    const filters = updateFilters(
      this.getFilterName(),
      UserFilter.TimeTakenBetween,
      [userTimeTakenBetween(this.getAnalyticsForCalculation(), minTime, maxTime)],
      true,
      [minTime, maxTime].map(timeTakenFormatter),
      this.state.filters
    );
    this.setState({ filters, showFilteredTable: false }, this.updateSelectedUsers);
  };

  private handleAssessmentsChartFilter = (status: AssessmentStatus) => (assessmentCategory: AssessmentType) => {
    const label = this.getAssessmentChartFilterLabel(status, assessmentCategory);
    const filters = updateFilterConditions(
      this.getFilterName(),
      UserFilter.AssessmentState,
      getUserAssessmentFilter(label)(this.getAnalyticsForCalculation()),
      true,
      label,
      this.state.filters
    );
    this.updateOrRemoveItemFromArray(filters, label, "assessmentChartLabels");
  };

  private updateOrRemoveItemFromArray = (
    filters: IUserFilter[],
    value: ArrayValue,
    key: "assessmentChartLabels" | "topicsChartSelected"
  ) => {
    const searchArray: ArrayValue[] = this.state[key];
    const updatedState = { filters };
    if (searchArray.includes(value)) {
      this.setState(
        R.set(R.lensPath([key]), R.remove<ArrayValue>(searchArray.indexOf(value), 1, searchArray))(updatedState),
        this.updateSelectedUsers
      );
    } else {
      this.setState(
        R.set(R.lensPath([key]), R.append<ArrayValue>(value, searchArray))(updatedState),
        this.updateSelectedUsers
      );
    }
    this.setState({ showFilteredTable: false });
  };

  private getAssessmentChartFilterLabel = (status: AssessmentStatus, assessmentCategory: AssessmentType) => {
    if (AssessmentStatus.Passed === status) {
      if (assessmentCategory === AssessmentType.PreAssessment) {
        return AssessmentChartFilterLabels.PassedPreAssessment;
      }
      return AssessmentChartFilterLabels.PassedPostAssessment;
    }
    if (assessmentCategory === AssessmentType.PreAssessment) {
      return AssessmentChartFilterLabels.FailedPreAssessment;
    }
    return AssessmentChartFilterLabels.FailedPostAssessment;
  };

  private handleTopicsChartFilter: TopicsChartFilter = (topics: number) => {
    const filters = updateFilterConditions(
      this.getFilterName(),
      UserFilter.TookTopic,
      userTookTopic(this.getAnalyticsForCalculation())(topics),
      true,
      topics.toString(),
      this.state.filters
    );
    this.updateOrRemoveItemFromArray(filters, topics, "topicsChartSelected");
  };

  private onSelectUnitName = (selectedUnitName: string | null) => () => {
    this.setState({ selectedUnitName });
  };

  private getAnalyticsForCalculation = () => {
    const { selectedUnitName } = this.state;
    const { unitsAnalytics, usersAnalytics } = this.props;
    const defaultAnalytics = usersAnalytics || {};
    if (selectedUnitName === null) {
      return defaultAnalytics;
    }
    if (unitsAnalytics === null) {
      return defaultAnalytics;
    }
    const usersUnitAnalytics = {};
    for (const unitAnalytics of unitsAnalytics) {
      if (unitAnalytics.name === selectedUnitName) {
        Object.entries(defaultAnalytics).forEach(([k, v]) => {
          const userUnitAnalytics = unitAnalytics.analytics[k];
          const analytics = userUnitAnalytics ? userUnitAnalytics : {};
          usersUnitAnalytics[k] = { ...v, ...analytics };
        });
        return usersUnitAnalytics;
      }
    }
    return defaultAnalytics;
  };

  private getFilterName = (): string => {
    const { selectedUnitName } = this.state;
    if (selectedUnitName === null) {
      return "course";
    }
    return `unit "${selectedUnitName}"`;
  };

  private onSelectLocale = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLocale = LocaleNames[event.target.value] ? (event.target.value as PreferredLocale) : undefined;

    this.setState({ selectedLocale });
  };

  private selectAssessmentName = (
    selectedAssessmentName: string | null,
    selectedAssessmentIdentifier: string | null,
    selectedAssessmentType: UnitChartType | null,
    unitInstanceAnalytics?: IUnitInstanceAnalytics
  ) => () => {
    if (!unitInstanceAnalytics) {
      this.setState({
        selectedAssessmentIdentifier,
        selectedAssessmentName,
        selectedAssessmentType,
        unitInstanceAnalytics: {},
      });
    } else {
      this.setState({
        selectedAssessmentIdentifier,
        selectedAssessmentName,
        selectedAssessmentType,
        unitInstanceAnalytics,
      });
    }
  };

  private changeAnalyticsView = (view: AnalyticsViews) => () => this.setState({ view });
}
